import type {OrderOptions} from '@github-ui/dashboard-sorting-dropdown/SortingDropdown'
import {findSelectedSubOption, getSortingKeyAndDirection} from '@github-ui/dashboard-sorting-dropdown/SortingDropdown'

export const DEFAULT_SORTING_KEY = 'updated'

export const defaultSortingOptions: Record<string, OrderOptions> = {
  created: {
    label: 'Created on',
    buttonLabel: {asc: 'Oldest', desc: 'Newest'},
    directionLabels: {asc: 'Oldest', desc: 'Newest'},
    defaultDirection: 'desc',
  },
  updated: {
    label: 'Last updated',
    buttonLabel: 'Updated',
    directionLabels: {asc: 'Oldest', desc: 'Newest'},
    defaultDirection: 'desc',
  },
  comments: {label: 'Total comments', buttonLabel: 'Comments', defaultDirection: 'desc'},
  relevance: {label: 'Best match', defaultDirection: undefined},
}

export const REACTIONS_SORT_KEY = 'reactions'

/**
 * Reaction sub-options additionally carry the reaction `content` (the GraphQL
 * `ReactionContent` enum value) used to match against an issue's reaction groups.
 * This is consumer-specific and intentionally not part of the generic dropdown's
 * `OrderOptions`, which only renders `label` and `emoji`.
 */
interface IssuesOrderOptions extends OrderOptions {
  subOptions?: Record<string, {label: string; emoji?: string; content?: string; value?: string}>
}

export const issuesDashboardSortingOptions: Record<string, IssuesOrderOptions> = {
  [REACTIONS_SORT_KEY]: {
    label: 'Reactions',
    defaultDirection: 'desc',
    subOptions: {
      all: {label: 'Total reactions', value: REACTIONS_SORT_KEY},
      '+1': {label: 'Thumbs up', emoji: '👍', content: 'THUMBS_UP'},
      '-1': {label: 'Thumbs down', emoji: '👎', content: 'THUMBS_DOWN'},
      smile: {label: 'Laugh', emoji: '😄', content: 'LAUGH'},
      tada: {label: 'Hooray', emoji: '🎉', content: 'HOORAY'},
      thinking_face: {label: 'Confused', emoji: '😕', content: 'CONFUSED'},
      rocket: {label: 'Rocket', emoji: '🚀', content: 'ROCKET'},
      eyes: {label: 'Eyes', emoji: '👀', content: 'EYES'},
      heart: {label: 'Heart', emoji: '❤️', content: 'HEART'},
    },
  },
}

/**
 * Whether the active sort key is the "updated" sort, in either direction (e.g. `updated`,
 * `updated-asc`, `updated-desc`). Callers must resolve an empty (default) sort key first.
 */
export function isUpdatedSort(sortingItemSelected: string | undefined): boolean {
  if (!sortingItemSelected) return false

  const [field] = getSortingKeyAndDirection(sortingItemSelected, DEFAULT_SORTING_KEY)
  return field === 'updated'
}

/**
 * Resolves the reaction to display for a row from the active sort key. Returns the
 * matching reaction group `content`, `emoji`, and `label`, or `undefined` when the
 * sort is not a reaction sort. Shared by issue and pull request rows so the mixed
 * dashboard view renders the same reaction the list is sorted by.
 */
export function getReactionSortDisplay(
  sortingItemSelected: string | undefined,
): {content?: string; emoji?: string; label: string} | undefined {
  if (!sortingItemSelected) return undefined

  const [field] = getSortingKeyAndDirection(sortingItemSelected, DEFAULT_SORTING_KEY)
  const selected = findSelectedSubOption(field, issuesDashboardSortingOptions)
  if (selected?.parentKey !== REACTIONS_SORT_KEY) return undefined

  const subOption = issuesDashboardSortingOptions[REACTIONS_SORT_KEY]?.subOptions?.[selected.subKey]
  if (!subOption) return undefined

  return {
    content: subOption.content,
    emoji: subOption.emoji,
    label: subOption.content ? subOption.label : 'total',
  }
}
